import { useState, useEffect, useRef } from 'react';

interface UseStickyCTAOptions {
  targetSelector?: string;
  threshold?: number;
}

export const useStickyCTA = ({
  targetSelector = '.hero-cta-section',
  threshold = 0,
}: UseStickyCTAOptions = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const cachedPositionRef = useRef<number | null>(null);
  const tickingRef = useRef(false);

  useEffect(() => {
    const targetElement = document.querySelector(targetSelector);
    
    if (!targetElement) {
      console.warn(`useStickyCTA: Element with selector "${targetSelector}" not found`);
      return;
    }

    // Cache the initial position
    const updateCachedPosition = () => {
      const rect = targetElement.getBoundingClientRect();
      cachedPositionRef.current = rect.bottom + window.scrollY;
    };

    updateCachedPosition();

    // Recalculate position on resize
    const handleResize = () => {
      updateCachedPosition();
    };

    const handleScroll = () => {
      if (!tickingRef.current) {
        window.requestAnimationFrame(() => {
          if (cachedPositionRef.current !== null) {
            const scrollY = window.scrollY;
            const shouldShow = scrollY > cachedPositionRef.current - threshold;
            setIsVisible(shouldShow);
          }
          tickingRef.current = false;
        });
        tickingRef.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [targetSelector, threshold]);

  return isVisible;
};
